import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, DatePicker, Button, Segmented } from "antd";
import { DownloadOutlined, TeamOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

function BrandWorkload() {
  const { request } = useRequest();
  const [periodType, setPeriodType] = useState("month"); // "week" | "month"
  const [anchor, setAnchor] = useState(moment()); // any moment inside the selected week/month
  const [bmReport, setBmReport] = useState([]);
  const [bsReport, setBsReport] = useState([]);
  const [bmLoading, setBmLoading] = useState(false);
  const [bsLoading, setBsLoading] = useState(false);
  const [exportingKey, setExportingKey] = useState(null); // `${type}-${staffId}`
  const [exportingConsolidated, setExportingConsolidated] = useState(false);

  const range = () => {
    const from = anchor.clone().startOf(periodType).toISOString();
    const to = anchor.clone().endOf(periodType).toISOString();
    return { from, to };
  };

  const periodLabel = () =>
    periodType === "week"
      ? `${anchor.clone().startOf("week").format("MMM D")} – ${anchor.clone().endOf("week").format("MMM D, YYYY")}`
      : anchor.format("MMMM YYYY");

  const fetchBM = useCallback(() => {
    setBmLoading(true);
    const { from, to } = range();
    request({
      url: `${apiPath.brandWorkloadBM}?from=${from}&to=${to}`,
      method: "GET",
      onSuccess: (data) => {
        setBmLoading(false);
        setBmReport(data?.data?.report || []);
      },
      onError: (err) => {
        setBmLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to load Brand Manager workload", Severty.ERROR);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType, anchor]);

  const fetchBS = useCallback(() => {
    setBsLoading(true);
    const { from, to } = range();
    request({
      url: `${apiPath.brandWorkloadBS}?from=${from}&to=${to}`,
      method: "GET",
      onSuccess: (data) => {
        setBsLoading(false);
        setBsReport(data?.data?.report || []);
      },
      onError: (err) => {
        setBsLoading(false);
        ShowToast(err?.response?.data?.message || "Failed to load Brand Strategist workload", Severty.ERROR);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType, anchor]);

  useEffect(() => {
    fetchBM();
    fetchBS();
  }, [fetchBM, fetchBS]);

  const exportReport = async (type, staffId, name) => {
    const key = `${type}-${staffId}`;
    setExportingKey(key);
    try {
      const token = localStorage.getItem("admin_token") || "";
      const { from, to } = range();
      const pathBuilder = type === "bm" ? apiPath.brandWorkloadBMExport : apiPath.brandWorkloadBSExport;
      const url = `${apiPath.baseURL}/${pathBuilder(staffId)}?from=${from}&to=${to}`;
      const response = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${type === "bm" ? "BM" : "BS"}_Workload_${name.replace(/\s+/g, "_")}_${anchor.format("YYYY-MM-DD")}_${periodType}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      ShowToast("Failed to export workload report", Severty.ERROR);
    } finally {
      setExportingKey(null);
    }
  };

  const exportConsolidated = async () => {
    setExportingConsolidated(true);
    try {
      const token = localStorage.getItem("admin_token") || "";
      const { from, to } = range();
      const url = `${apiPath.baseURL}/${apiPath.brandWorkloadConsolidatedExport}?from=${from}&to=${to}`;
      const response = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Brand_Team_Workload_Consolidated_${anchor.format("YYYY-MM-DD")}_${periodType}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      ShowToast("Failed to export consolidated report", Severty.ERROR);
    } finally {
      setExportingConsolidated(false);
    }
  };

  const bmColumns = [
    { title: "Brand Manager", dataIndex: "name", key: "name" },
    { title: "Assigned Clients", dataIndex: "clientCount", key: "clientCount" },
    { title: "Posts Created", dataIndex: "postsCreated", key: "postsCreated" },
    { title: "Blogs Created", dataIndex: "blogsCreated", key: "blogsCreated" },
    { title: "Total Created", dataIndex: "totalCreated", key: "totalCreated" },
    { title: "Posts Published", dataIndex: "postsPublished", key: "postsPublished" },
    { title: "Blogs Published", dataIndex: "blogsPublished", key: "blogsPublished" },
    { title: "Total Published", dataIndex: "totalPublished", key: "totalPublished" },
    { title: "Target (Posts)", dataIndex: "targetPosts", key: "targetPosts" },
    { title: "Target (Blogs)", dataIndex: "targetBlogs", key: "targetBlogs" },
    { title: "Target (Total)", dataIndex: "targetTotal", key: "targetTotal" },
    {
      title: "% of Target",
      key: "pctOfTarget",
      render: (_, r) => (r.targetTotal > 0 ? `${Math.round((r.totalPublished / r.targetTotal) * 100)}%` : "-"),
    },
    {
      title: "Export",
      key: "export",
      width: 170,
      render: (_, record) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={exportingKey === `bm-${record.staffId}`}
          onClick={() => exportReport("bm", record.staffId, record.name)}
        >
          Download Report
        </Button>
      ),
    },
  ];

  const bsColumns = [
    { title: "Brand Strategist", dataIndex: "name", key: "name" },
    { title: "Posts Received", dataIndex: "postsReceived", key: "postsReceived" },
    { title: "Posts Approved", dataIndex: "postsApproved", key: "postsApproved" },
    { title: "Blogs Received", dataIndex: "blogsReceived", key: "blogsReceived" },
    { title: "Blogs Approved", dataIndex: "blogsApproved", key: "blogsApproved" },
    {
      title: "Export",
      key: "export",
      width: 170,
      render: (_, record) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={exportingKey === `bs-${record.staffId}`}
          onClick={() => exportReport("bs", record.staffId, record.name)}
        >
          Download Report
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="gfx-req-hero gfx-oversight-hero">
        <div className="gfx-req-hero-top">
          <div>
            <div className="gfx-req-hero-eyebrow">Brand Team</div>
            <h2 className="gfx-req-hero-title">Workload &amp; Reporting</h2>
          </div>
          <div className="gfx-hero-done">
            <div className="gfx-hero-done-ring">
              <TeamOutlined />
            </div>
            <div>
              <div className="gfx-hero-done-value">{bmReport.length + bsReport.length}</div>
              <div className="gfx-hero-done-label">Team Members</div>
            </div>
          </div>
        </div>
      </div>

      <Card bordered={false} className="criclebox mb-24 gfx-oversight-toolbar-card">
        <div className="gfx-oversight-toolbar" style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Segmented
              options={[
                { label: "Week", value: "week" },
                { label: "Month", value: "month" },
              ]}
              value={periodType}
              onChange={(v) => setPeriodType(v)}
            />
            <DatePicker
              picker={periodType}
              value={anchor}
              allowClear={false}
              onChange={(m) => setAnchor(m || moment())}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={exportingConsolidated}
            onClick={exportConsolidated}
          >
            Download Consolidated Report
          </Button>
        </div>
        <p style={{ color: "#888", marginTop: 12, marginBottom: 0 }}>
          Showing <b>{periodLabel()}</b>. Target figures come from each client's Posting Schedule preference (set
          during onboarding), prorated to this exact date range. Frequencies marked "Custom" are free text and can't
          be converted to a number, so they're excluded from the target — only "per week" / "per month" entries are
          counted.
        </p>
      </Card>

      <Card bordered={false} className="criclebox tablespace mb-24" title="Brand Managers">
        <Table
          rowKey="staffId"
          loading={bmLoading}
          columns={bmColumns}
          dataSource={bmReport}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Card bordered={false} className="criclebox tablespace mb-24" title="Brand Strategists">
        <Table rowKey="staffId" loading={bsLoading} columns={bsColumns} dataSource={bsReport} pagination={{ pageSize: 10 }} />
      </Card>
    </>
  );
}

export default BrandWorkload;
